import { useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  selectSortedTechKeys,
  selectAlphaSortedTechKeys,
  selectTechniques,
  selectPersonalWiki,
  selectIsLoading,
  selectError,
  selectTechniqueWithStats,
  loadPersonalWiki,
  resetTrainingWiki,
  clearAllListeners
} from '../store/slices/trainingWikiSlice';

/**
 * Hook to access training wiki data and actions
 * Matches the original app's data structure and behavior
 */
export const useTrainingWiki = () => {
  const dispatch = useAppDispatch();
  
  // Selectors
  const sortedTechKeys = useAppSelector(selectSortedTechKeys);
  const alphaSortedTechKeys = useAppSelector(selectAlphaSortedTechKeys);
  const techniques = useAppSelector(selectTechniques);
  const personalWiki = useAppSelector(selectPersonalWiki);
  const isLoading = useAppSelector(selectIsLoading);
  const error = useAppSelector(selectError);
  
  // Actions
  const refreshPersonalWiki = useCallback((userId: string) => {
    return dispatch(loadPersonalWiki(userId));
  }, [dispatch]);
  
  const getTechniqueWithStats = useCallback((techniqueKey: string) => {
    return selectTechniqueWithStats(techniqueKey);
  }, []);
  
  const cleanup = useCallback(() => {
    dispatch(clearAllListeners());
    dispatch(resetTrainingWiki());
  }, [dispatch]);
  
  return {
    // Data
    sortedTechKeys,
    alphaSortedTechKeys,
    techniques,
    personalWiki,
    userTechniques: personalWiki, // Alias for compatibility
    trainingWikiTechniques: techniques, // Alias for compatibility
    isLoading,
    error,
    
    // Actions
    refreshPersonalWiki,
    getTechniqueWithStats,
    cleanup,
    
    // Compatibility functions (no-ops or simplified)
    getTechniques: () => Promise.resolve(),
    getUserTechniques: () => Promise.resolve(personalWiki),
    getTechniqueById: () => Promise.resolve(null),
    searchTechniques: () => Promise.resolve([]),
    createTechnique: () => Promise.reject('Not implemented'),
    updateTechniqueData: () => Promise.reject('Not implemented'),
    removeTechnique: () => Promise.reject('Not implemented'),
    saveTechniqueToCollection: () => Promise.reject('Not implemented'),
    removeTechniqueFromCollection: () => Promise.reject('Not implemented'),
    updateUserTechnique: () => Promise.reject('Not implemented')
  };
};